import { supabase } from '../lib/supabase';
import { Department, Participant } from '../types/models';

export const getDepartments = async (): Promise<Department[]> => {
  const { data, error } = await supabase
    .from('departments')
    .select(`
      *,
      students:students(
        student_id,
        first_name,
        last_name,
        first_name_tamil,
        last_name_tamil,
        date_of_birth,
        gender,
        house:houses(
          house_id,
          house_name,
          house_color
        )
      )
    `)
    .order('name');
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data || [];
};

export const getDepartmentById = async (id: string): Promise<Department | null> => {
  const { data, error } = await supabase
    .from('departments')
    .select(`
      *,
      students:students(
        student_id,
        first_name,
        last_name,
        first_name_tamil,
        last_name_tamil,
        date_of_birth,
        gender,
        house:houses(
          house_id,
          house_name,
          house_color
        )
      )
    `)
    .eq('department_id', id)
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const createDepartment = async (department: Omit<Department, 'department_id' | 'created_at'>): Promise<Department> => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can create departments');
  }

  const { data, error } = await supabase
    .from('departments')
    .insert([department])
    .select()
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const updateDepartment = async (id: string, department: Partial<Omit<Department, 'department_id' | 'created_at'>>): Promise<Department> => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can update departments');
  }

  const { data, error } = await supabase
    .from('departments')
    .update(department)
    .eq('department_id', id)
    .select()
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const deleteDepartment = async (id: string): Promise<void> => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can delete departments');
  }

  const { error } = await supabase
    .from('departments')
    .delete()
    .eq('department_id', id);
    
  if (error) {
    throw new Error(error.message);
  }
};

export const toggleDepartmentVisibility = async (id: string, hidden: boolean): Promise<Department> => {
  // Check if user has admin or manager role
  const { data: userRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  if (rolesError) {
    throw new Error('Failed to verify permissions');
  }

  const hasRequiredRole = userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  );

  if (!hasRequiredRole) {
    throw new Error('Unauthorized: Only administrators and managers can update department visibility');
  }

  const { data, error } = await supabase
    .from('departments')
    .update({ hidden_in_exports: hidden })
    .eq('department_id', id)
    .select()
    .single();
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data;
};

export const getVisibleDepartments = async (): Promise<Department[]> => {
  const { data, error } = await supabase
    .from('departments')
    .select('*')
    .eq('hidden_in_exports', false)
    .order('name');
    
  if (error) {
    throw new Error(error.message);
  }
  
  return data || [];
};

export const checkUserCanManageDepartments = async (): Promise<boolean> => {
  const { data: userRoles } = await supabase
    .from('user_roles')
    .select(`
      roles!inner(
        role_name
      )
    `)
    .eq('user_id', (await supabase.auth.getUser()).data.user?.id);

  return userRoles?.some(ur => 
    ur.roles.role_name === 'admin' || ur.roles.role_name === 'manager'
  ) ?? false;
};